import {FormControl, FormField} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';

import {Path, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {GetSalesPlatformsApiRes} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {TestIdProps} from 'shared/web';

import {GeneralSettingsFormValues} from '../types/GeneralSettingsFormValues';
import {PlatformTitleLimitChip} from './PlatformTitleLimitChip';

interface SubtitleFormFieldProps extends TestIdProps {
  control: FormControl<GeneralSettingsFormValues>;
  name: Path<GeneralSettingsFormValues>;
  isDisabled?: boolean;
  platforms: GetSalesPlatformsApiRes | undefined;
}

export function SubtitleFormField(props: SubtitleFormFieldProps) {
  // FormField owns controller registration.
  const watchedValue = useWatch({name: props.name, control: props.control});
  const fieldValue = typeof watchedValue === 'string' ? watchedValue : '';

  // Only enabled platforms with subtitle limits participate.
  const chips = (props.platforms ?? [])
    .filter((platformData) => platformData.platform.enabled && platformData.enabled)
    .flatMap((platformData) => {
      const limit = platformData.platform.subtitleLimit;
      return isNotNil(limit)
        ? [{key: platformData.platform.code, label: platformData.platform.title, limit}]
        : [];
    });

  if (chips.length === 0) {
    return null;
  }

  // Hard-stop at the highest enabled platform limit.
  const inputMaxLength = Math.max(...chips.map((chip) => chip.limit));

  return (
    <VStack spacing={1}>
      <FormField
        control={props.control}
        label={i18n.t('page.advertisement.labels.subtitle')}
        name={props.name}
        type="text"
        isDisabled={props.isDisabled}
        tooltip={i18n.t('page.advertisement.labels.subtitleTooltip')}
        maxLength={inputMaxLength}
        suffix={`${fieldValue.length} / ${inputMaxLength}`}
        data-testid={props['data-testid']}
      />
      <HStack spacing={3} align="center" wrap>
        {chips.map((chip) => (
          <PlatformTitleLimitChip
            key={chip.key}
            label={chip.label}
            limit={chip.limit}
            value={fieldValue}
          />
        ))}
      </HStack>
    </VStack>
  );
}
